import type { PaymentDialogCustomerSelection } from "../../ui/PaymentDialog";
import { posApi } from "../../api/client";
import { findLocalCustomerByPhone, saveLocalCustomerDraft } from "./db";
import {
  getCachedCustomerByPhone,
  saveCustomerToReferenceCache,
} from "./reference-cache";

export type ResolveLocalFirstCustomerResult = {
  selection: PaymentDialogCustomerSelection;
  source: "server" | "cache" | "local_draft";
};

type ResolveLocalFirstCustomerInput = {
  branchId: string;
  deviceId?: string;
  phone: string;
  findOnline: () => Promise<{
    id: string;
    firstName: string;
    lastName: string;
    phone: string | null;
  }>;
  allowLocalDraft?: boolean;
};

export async function resolveLocalFirstCustomerByPhone(
  input: ResolveLocalFirstCustomerInput,
): Promise<ResolveLocalFirstCustomerResult | null> {
  const trimmedPhone = input.phone.trim();
  if (!trimmedPhone) return null;

  try {
    const customer = await input.findOnline();
    const capturedAt = new Date().toISOString();
    let loyaltyPoints: number | undefined;
    try {
      loyaltyPoints = await posApi.getCustomerLoyaltyBalance(customer.id);
    } catch {
      // balance fetch is best-effort; cache still stores customer identity
    }
    await saveCustomerToReferenceCache(
      input.branchId,
      {
        customerId: customer.id,
        phone: customer.phone ?? trimmedPhone,
        firstName: customer.firstName,
        lastName: customer.lastName,
        loyaltyPoints,
      },
      { deviceId: input.deviceId },
    );

    return {
      source: "server",
      selection: {
        id: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        phone: customer.phone,
        source: "server",
        capturedAt,
        customerSnapshot: {
          customerId: customer.id,
          phone: customer.phone ?? trimmedPhone,
          firstName: customer.firstName,
          lastName: customer.lastName,
          source: "server",
          capturedAt,
        },
      },
    };
  } catch {
    const cached = await getCachedCustomerByPhone(input.branchId, trimmedPhone);
    if (cached) {
      const capturedAt = new Date().toISOString();
      return {
        source: "cache",
        selection: {
          id: cached.customerId,
          firstName: cached.firstName ?? "",
          lastName: cached.lastName ?? "",
          phone: cached.phone,
          source: "cache",
          capturedAt,
          customerSnapshot: {
            customerId: cached.customerId,
            phone: cached.phone,
            firstName: cached.firstName,
            lastName: cached.lastName,
            source: "cache",
            capturedAt,
          },
        },
      };
    }

    if (input.allowLocalDraft === false) {
      return null;
    }

    const existing = await findLocalCustomerByPhone(
      input.branchId,
      trimmedPhone,
    );
    const draft =
      existing ??
      (await saveLocalCustomerDraft({
        branchId: input.branchId,
        deviceId: input.deviceId,
        phone: trimmedPhone,
      }));

    return {
      source: "local_draft",
      selection: {
        id: draft.serverCustomerId ?? "",
        localCustomerId: draft.localCustomerId,
        firstName: draft.firstName ?? "",
        lastName: draft.lastName ?? "",
        phone: draft.phone,
        source: "local_draft",
        capturedAt: draft.customerSnapshot.capturedAt,
        customerSnapshot: draft.customerSnapshot,
      },
    };
  }
}
